import { Box, SwipeableDrawer, TextField, Typography } from "@mui/material";
import { type FC, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useCallInReasonOptions } from "../../../components/team/CallInReasons/callInReasonsHooks";
import { SystemDropdown } from "../../../components/UIToolkit/Inputs/SystemDropDown/SystemDropdown";
import { InlineHint } from "../../../components/UIToolkit/molecules/InlineHint";
import SystemButton from "../../../components/UIToolkit/SystemButton/SystemButton";
import { useAnalytics } from "../../../contexts/analytics/hooks";
import { useSnackbar } from "../../../contexts/SnackbarContext";
import { useCallOffShift } from "../../../hooks/useCallOffShift";
import { INK } from "../../../theme/tokens";
import { isNurseSelectableReason, isOtherReasonLabel } from "../../../utils/callIns";
import type { TransformedShift } from "../../../utils/models";

interface CallOffDrawerProps {
	open: boolean;
	onClose: () => void;
	shift: TransformedShift | null;
	nurseId: number;
}

const CallOffDrawer: FC<CallOffDrawerProps> = ({ open, onClose, shift, nurseId }) => {
	const { t } = useTranslation();
	const { showSnackbar } = useSnackbar();

	const { track, trackError } = useAnalytics();
	// The nurse's shifts span departments (float pool), so the list — and the label we persist — must come
	// from the department of THIS shift, not from whichever department is currently chosen in the app.
	const {
		options: reasonOptions,
		isLoading: isLoadingReasons,
		isError: isReasonsError,
		isSuccess: isReasonsLoaded,
	} = useCallInReasonOptions({
		departmentId: shift?.departmentId,
		enabled: !!shift?.departmentId,
		filter: isNurseSelectableReason,
		translateLabels: true,
	});
	const [pickedReason, setPickedReason] = useState<string | null>(null);
	const [otherReason, setOtherReason] = useState("");

	// `ShiftCard` renders this drawer ONCE per nurse and toggles `open`, so it never unmounts and this
	// state outlives the shift it was typed for. The `reasonOptions.some(...)` check below catches a
	// picked reason the next department doesn't have, but not one it does — "Other" is in every stock
	// list — and nothing at all catches the free text, so the previous shift's explanation would ride
	// along onto the next call-off. Reset on the opening edge rather than on close: closing can happen
	// by swipe, backdrop or a successful submit, and only one of those runs `onClose` reliably.
	useEffect(() => {
		if (!open || !shift?.id) return;
		setPickedReason(null);
		setOtherReason("");
	}, [open, shift?.id]);

	// DEFAULT: derived rather than seeded into state, so the first reason in the department's own order is
	// the default as soon as the query resolves — and a drawer re-opened on another department's shift can
	// never submit a label that department doesn't have.
	const reason = pickedReason && reasonOptions.some((option) => option.value === pickedReason) ? pickedReason : (reasonOptions[0]?.value ?? null);
	const isOtherSelected = isOtherReasonLabel(reason);
	// "Nothing configured", "still loading" and "we couldn't load it" all look like an empty picker but are
	// not the same fact, so they get different treatments — telling a nurse her unit has no reasons when the
	// request failed would send her to a head nurse who has nothing to fix. Keyed off `isSuccess`, the only
	// state that actually means the server said "none".
	const hasNoConfiguredReasons = isReasonsLoaded && reasonOptions.length === 0;

	const { mutate: callOffShift, isPending } = useCallOffShift({
		onSuccess: () => {
			showSnackbar(t("success.call.off"), "success");
			onClose();
		},
	});

	// A nurse who opens Call Out and cannot call off at all is invisible otherwise — it looks like silence,
	// not like a department that never configured its reasons. Same for a failed read, which is the shape a
	// missing permission would take. Fires on the edge, so once per open drawer, not once per render.
	useEffect(() => {
		if (!open || !shift?.departmentId) return;
		// An unconfigured department is a state, so it goes through `track`; a failed read is an error, so it
		// goes through `trackError` — only the latter carries `__severity: "error"`, which is what the error
		// surfaces filter on. Sending it via `track` would bury the one signal that this GET is failing.
		if (hasNoConfiguredReasons) track("call_off_reasons_empty", { department_id: shift.departmentId });
		else if (isReasonsError) trackError("call_off_reasons_load_failed", { department_id: shift.departmentId });
	}, [open, shift?.departmentId, hasNoConfiguredReasons, isReasonsError, track, trackError]);

	if (!shift) {
		return null;
	}

	const handleCallOff = () => {
		if (!shift.id || !shift.departmentId || !reason) return;

		callOffShift({
			departmentId: shift.departmentId,
			shiftId: shift.id,
			nurseId: nurseId,
			// Always the untranslated department label — what the picker shows may be translated, what we
			// persist to `nurse_in_shift.message` never is.
			reason: isOtherSelected ? otherReason : reason,
		});
	};

	return (
		<SwipeableDrawer
			data-testid="call-off-drawer"
			anchor="bottom"
			onOpen={() => {}}
			open={open}
			onClose={onClose}
			slotProps={{
				paper: {
					sx: { borderTopLeftRadius: 16, borderTopRightRadius: 16, p: 3 },
				},
			}}
		>
			<Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
				<Typography
					variant="h6"
					sx={{
						fontWeight: 700,
						color: INK.SECONDARY,
					}}
				>
					{t("call.out.reason")}
				</Typography>

				<Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
					<Typography
						variant="body2"
						sx={(theme) => ({
							fontWeight: 600,
							color: theme.palette.primary.main,
						})}
					>
						{t("reason")}
					</Typography>
					<SystemDropdown
						data-testid="call-off-reason-select"
						fullWidth
						items={reasonOptions}
						// `undefined`, not `""` — SystemDropdown treats a nullish value as "nothing chosen", while `""`
						// becomes a phantom id that makes `hasSelection` true with nothing actually selected.
						selectedItem={reason ?? undefined}
						onChange={(value) => {
							if (value !== undefined) setPickedReason(value);
						}}
						loading={isLoadingReasons}
						disabled={isLoadingReasons || reasonOptions.length === 0}
						// Says why the control is empty instead of leaving a blank box that reads as broken — but only
						// once we KNOW it is empty, so it can't contradict the hints below while the read is in flight.
						placeholder={hasNoConfiguredReasons ? t("call.off.no.reasons.placeholder") : undefined}
					/>
					<InlineHint open={hasNoConfiguredReasons} dataTestId="call-off-no-reasons-hint">
						{t("call.off.no.reasons.configured")}
					</InlineHint>
					<InlineHint open={isReasonsError} tone="warning" dataTestId="call-off-reasons-error-hint">
						{t("error.general")}
					</InlineHint>
				</Box>

				{isOtherSelected && (
					<Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
						<Typography
							variant="body2"
							sx={{
								fontWeight: 600,
								color: "primary.main",
							}}
						>
							{t("please.specify")}
						</Typography>
						<TextField
							data-testid="call-off-other-reason"
							fullWidth
							multiline
							rows={3}
							value={otherReason}
							onChange={(e) => setOtherReason(e.target.value)}
							placeholder={t("enter.reason")}
							sx={{
								"& .MuiOutlinedInput-root": {
									borderRadius: "8px",
								},
							}}
						/>
					</Box>
				)}

				<Box sx={{ display: "flex", gap: 2, mt: 1 }}>
					<SystemButton data-testid="call-off-cancel" buttonVariant={"secondary"} onClick={onClose} disabled={isPending}>
						{t("cancel")}
					</SystemButton>
					<SystemButton
						data-testid="call-off-submit"
						buttonVariant="primary"
						onClick={handleCallOff}
						disabled={isPending || isLoadingReasons || !reason || (isOtherSelected && !otherReason.trim())}
					>
						{isPending ? t("submitting") : t("submit")}
					</SystemButton>
				</Box>
			</Box>
		</SwipeableDrawer>
	);
};

export default CallOffDrawer;
